/**
 * BureauFormEditor — full free-form editor for the pre-filled TransUnion
 * and Equifax dispute forms (Feb 2026 user request).
 *
 * The user asked for the ability to edit ANY field on the generated
 * bureau forms — nudge a name / SSN digit, un-check or add a
 * checkbox, white-out an auto-filled value, drop a fresh signature.
 * Rather than a rigid "coordinate override" UI, we ship a truly free-
 * form editor: pages render as images, and the user can drop text,
 * checkmarks, erase rectangles, and signatures anywhere they want.
 *
 * Storage model:
 *   • ``basePdfDataUrl`` — the generated PDF from the /generate call
 *   • ``edits`` — a JSON array of {type, page, coords, …} overlays
 *   • ``pages`` — server-rendered PNG previews per PDF page
 *
 * Editor tools:
 *   • text   — click to drop a text field, drag to move, dbl-click to edit
 *   • check  — click to drop a checkmark X, drag to move
 *   • erase  — click-drag to paint a white rectangle over an auto-fill
 *   • hand   — pan / no-op
 *
 * Coordinates are captured in PDF points (top-left origin) so the
 * backend applies them 1:1 — no display-scale drift.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Type as TypeIcon,
  CheckSquare,
  Eraser,
  MousePointer2,
  Loader2,
  Download,
  Trash2,
  RotateCcw,
  X as XIcon,
  Save,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOOLS = [
  { id: "select", label: "Select / Move",  icon: MousePointer2 },
  { id: "text",   label: "Add Text",        icon: TypeIcon },
  { id: "check",  label: "Add Checkmark",   icon: CheckSquare },
  { id: "erase",  label: "White-out",       icon: Eraser },
];

// Generate a stable-ish local id for each overlay so we can select/edit.
const localId = () => `e_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export default function BureauFormEditor({
  open,
  onClose,
  basePdfDataUrl,   // "data:application/pdf;base64,…"
  filename,         // default download name (e.g. "Equifax_Dispute_Form.pdf")
  bureauLabel,      // "TransUnion" | "Equifax"
}) {
  const [pages, setPages] = useState([]);            // [{page, width_pts, image_data_url, …}]
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tool, setTool] = useState("select");
  const [edits, setEdits] = useState([]);            // [{id, type, page, x, y, text?, fontsize?, size?, x0?, y0?, x1?, y1?}]
  const [selectedId, setSelectedId] = useState(null);
  const [dragState, setDragState] = useState(null);  // {id, startX, startY, origX, origY}
  const [eraseDraft, setEraseDraft] = useState(null); // {page, startPtX, startPtY, curPtX, curPtY}
  const [textFontSize, setTextFontSize] = useState(11);
  const [checkSize, setCheckSize] = useState(9);       // matches the 8.5-9pt pre-fill size
  const pageRefs = useRef({});                        // pageIdx -> div ref

  // Load pages when the editor opens with a new PDF.
  useEffect(() => {
    if (!open || !basePdfDataUrl) return;
    setEdits([]);
    setSelectedId(null);
    setPages([]);
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/bureau-dispute-forms/render-pages`,
          { pdf_data_url: basePdfDataUrl, dpi: 150 },
          { timeout: 60000 },
        );
        setPages(data?.pages || []);
      } catch (e) {
        console.error("[BureauFormEditor] render failed", e);
        toast.error("Failed to render form pages for editing.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, basePdfDataUrl]);

  // Convert an on-page mouse event → PDF points (top-left origin).
  const eventToPagePoint = (e, pageIdx) => {
    const info = pages.find((p) => p.page === pageIdx);
    const el = pageRefs.current[pageIdx];
    if (!info || !el) return null;
    const rect = el.getBoundingClientRect();
    const scaleX = info.width_pts / rect.width;
    const scaleY = info.height_pts / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
      // display-space is also useful for hit-testing existing overlays
      dx: e.clientX - rect.left,
      dy: e.clientY - rect.top,
      rectW: rect.width,
      rectH: rect.height,
    };
  };

  // Convert stored PDF-point coord → display px (so overlays match the
  // rendered image at whatever CSS width it's showing).
  const ptsToDisplay = (pageIdx, x, y) => {
    const info = pages.find((p) => p.page === pageIdx);
    const el = pageRefs.current[pageIdx];
    if (!info || !el) return { left: 0, top: 0, scale: 1 };
    const rect = el.getBoundingClientRect();
    const scale = rect.width / info.width_pts;
    return { left: x * scale, top: y * scale, scale };
  };

  const handlePageClick = (e, pageIdx) => {
    if (tool === "select") return;
    if (tool === "erase") return;   // erase uses drag, not click
    const pt = eventToPagePoint(e, pageIdx);
    if (!pt) return;
    if (tool === "text") {
      const txt = window.prompt("Text to insert:");
      if (!txt) return;
      const id = localId();
      setEdits((prev) => [
        ...prev,
        { id, type: "text", page: pageIdx, x: pt.x, y: pt.y, text: txt, fontsize: textFontSize },
      ]);
      setSelectedId(id);
      setTool("select");
    } else if (tool === "check") {
      const id = localId();
      // draw_check() renders around the (x, y) CENTER — no offset needed.
      setEdits((prev) => [
        ...prev,
        { id, type: "check", page: pageIdx, x: pt.x, y: pt.y, size: checkSize },
      ]);
      setSelectedId(id);
    }
  };

  // ─────────── Erase-rectangle drag capture ───────────
  const handlePageMouseDown = (e, pageIdx) => {
    if (tool !== "erase") return;
    const pt = eventToPagePoint(e, pageIdx);
    if (!pt) return;
    e.preventDefault();
    setEraseDraft({ page: pageIdx, startPtX: pt.x, startPtY: pt.y, curPtX: pt.x, curPtY: pt.y });
  };
  const handlePageMouseMove = (e, pageIdx) => {
    if (eraseDraft && eraseDraft.page === pageIdx) {
      const pt = eventToPagePoint(e, pageIdx);
      if (!pt) return;
      setEraseDraft((prev) => prev && { ...prev, curPtX: pt.x, curPtY: pt.y });
    }
  };
  const handlePageMouseUp = (e, pageIdx) => {
    if (!eraseDraft || eraseDraft.page !== pageIdx) return;
    const { startPtX, startPtY, curPtX, curPtY, page } = eraseDraft;
    setEraseDraft(null);
    const x0 = Math.min(startPtX, curPtX);
    const y0 = Math.min(startPtY, curPtY);
    const x1 = Math.max(startPtX, curPtX);
    const y1 = Math.max(startPtY, curPtY);
    if ((x1 - x0) < 2 || (y1 - y0) < 2) return;   // ignore accidental clicks
    const id = localId();
    setEdits((prev) => [
      ...prev,
      { id, type: "erase", page, x0, y0, x1, y1 },
    ]);
    setSelectedId(id);
  };

  // ─────────── Move handling for placed text/check overlays ───────────
  const beginDrag = (e, edit) => {
    if (tool !== "select") return;
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(edit.id);
    setDragState({ id: edit.id, startClientX: e.clientX, startClientY: e.clientY, origX: edit.x, origY: edit.y });
    const onMove = (mv) => {
      const info = pages.find((p) => p.page === edit.page);
      const el = pageRefs.current[edit.page];
      if (!info || !el) return;
      const rect = el.getBoundingClientRect();
      const scaleX = info.width_pts / rect.width;
      const scaleY = info.height_pts / rect.height;
      const dx = (mv.clientX - e.clientX) * scaleX;
      const dy = (mv.clientY - e.clientY) * scaleY;
      setEdits((prev) => prev.map((it) => (
        it.id === edit.id
          ? { ...it, x: edit.x + dx, y: edit.y + dy }
          : it
      )));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDragState(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const removeSelected = () => {
    if (!selectedId) return;
    setEdits((prev) => prev.filter((e) => e.id !== selectedId));
    setSelectedId(null);
  };

  const clearAll = () => {
    if (!edits.length) return;
    if (!window.confirm("Discard all edits and start over?")) return;
    setEdits([]);
    setSelectedId(null);
  };

  const undoLast = () => {
    setEdits((prev) => prev.slice(0, -1));
    setSelectedId(null);
  };

  const saveAndDownload = async () => {
    if (!basePdfDataUrl) return;
    try {
      setSaving(true);
      // Strip local ids before sending — backend doesn't need them.
      const payloadEdits = edits.map(({ id, ...rest }) => rest);
      const { data } = await axios.post(
        `${API}/bureau-dispute-forms/apply-edits`,
        { pdf_data_url: basePdfDataUrl, edits: payloadEdits, return_pages: false },
        { timeout: 90000 },
      );
      if (!data?.pdf_data_url) throw new Error("No PDF returned");
      const a = document.createElement("a");
      a.href = data.pdf_data_url;
      a.download = filename || `${(bureauLabel || "Bureau")}_Dispute_Form_edited.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Edited ${bureauLabel || "bureau"} form downloaded ✓`);
      onClose && onClose();
    } catch (e) {
      console.error("[BureauFormEditor] save failed", e);
      toast.error(e?.response?.data?.detail || e.message || "Failed to save edits");
    } finally {
      setSaving(false);
    }
  };

  const downloadOriginal = () => {
    if (!basePdfDataUrl) return;
    const a = document.createElement("a");
    a.href = basePdfDataUrl;
    a.download = filename || `${(bureauLabel || "Bureau")}_Dispute_Form.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Live-preview overlays grouped by page for render-time efficiency.
  const editsByPage = useMemo(() => {
    const m = new Map();
    edits.forEach((e) => {
      const arr = m.get(e.page) || [];
      arr.push(e);
      m.set(e.page, arr);
    });
    return m;
  }, [edits]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex flex-col"
      data-testid="bureau-form-editor"
    >
      {/* Top toolbar */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-zinc-950 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="text-white font-semibold truncate">
            Edit {bureauLabel || "Bureau"} Dispute Form
          </div>
          <div className="text-xs text-zinc-500 hidden sm:block">
            Free-form editor · click a tool, then click on the page
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            onClick={onClose}
            disabled={saving}
            data-testid="bureau-editor-close"
          >
            <XIcon className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            variant="outline"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            onClick={downloadOriginal}
            disabled={saving || loading}
            data-testid="bureau-editor-download-original"
          >
            <Download className="w-4 h-4 mr-1" /> Original
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
            onClick={saveAndDownload}
            disabled={saving || loading || !pages.length}
            data-testid="bureau-editor-save"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</>
            ) : (
              <><Save className="w-4 h-4 mr-1" /> Save & Download</>
            )}
          </Button>
        </div>
      </div>

      {/* Tool sidebar + page canvas */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        <div className="md:w-56 flex-shrink-0 bg-zinc-950/80 border-b md:border-b-0 md:border-r border-zinc-800 p-3 flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto">
          {TOOLS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTool(id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors flex-shrink-0 ${
                tool === id
                  ? "bg-emerald-600/25 border-emerald-500/60 text-emerald-100"
                  : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800"
              }`}
              data-testid={`bureau-editor-tool-${id}`}
            >
              <Icon className="w-4 h-4" /> <span className="whitespace-nowrap">{label}</span>
            </button>
          ))}

          <div className="md:mt-2 hidden md:flex md:flex-col gap-2 pt-2 border-t border-zinc-800">
            <label className="text-[11px] text-zinc-400 uppercase tracking-wide">Text size</label>
            <input
              type="number"
              min={6} max={40}
              value={textFontSize}
              onChange={(e) => setTextFontSize(Math.max(6, Math.min(40, parseFloat(e.target.value) || 11)))}
              className="w-full px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm"
              data-testid="bureau-editor-fontsize"
            />
            <label className="text-[11px] text-zinc-400 uppercase tracking-wide mt-2">Checkmark size</label>
            <input
              type="number"
              min={6} max={40}
              value={checkSize}
              onChange={(e) => setCheckSize(Math.max(6, Math.min(40, parseFloat(e.target.value) || 12)))}
              className="w-full px-2 py-1 rounded bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm"
              data-testid="bureau-editor-checksize"
            />
          </div>

          <div className="md:mt-auto flex md:flex-col gap-2 pt-2 md:border-t border-zinc-800">
            <button
              onClick={undoLast}
              disabled={!edits.length}
              className="flex-1 md:flex-none flex items-center gap-2 px-3 py-2 rounded-lg border text-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-40"
              data-testid="bureau-editor-undo"
            >
              <RotateCcw className="w-4 h-4" /> Undo
            </button>
            <button
              onClick={removeSelected}
              disabled={!selectedId}
              className="flex-1 md:flex-none flex items-center gap-2 px-3 py-2 rounded-lg border text-sm bg-red-950/40 border-red-900/40 text-red-300 hover:bg-red-950/60 disabled:opacity-40"
              data-testid="bureau-editor-delete"
            >
              <Trash2 className="w-4 h-4" /> Delete selected
            </button>
            <button
              onClick={clearAll}
              disabled={!edits.length}
              className="flex-1 md:flex-none flex items-center gap-2 px-3 py-2 rounded-lg border text-sm bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-40"
              data-testid="bureau-editor-clear"
            >
              Clear all
            </button>
          </div>
        </div>

        {/* Page canvas */}
        <div className="flex-1 overflow-auto p-4 bg-zinc-900/40">
          {loading && (
            <div className="flex items-center justify-center h-full text-zinc-300">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading form pages…
            </div>
          )}
          {!loading && !pages.length && (
            <div className="flex items-center justify-center h-full text-zinc-400 text-sm">
              No pages to render. Close and regenerate the form.
            </div>
          )}
          {!loading && pages.map((p) => {
            const overlays = editsByPage.get(p.page) || [];
            const cursor = tool === "select" ? "default" : (tool === "erase" ? "crosshair" : "cell");
            return (
              <div key={p.page} className="mb-6 max-w-[880px] mx-auto">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">
                  Page {p.page + 1} of {pages.length}
                </div>
                <div
                  ref={(el) => { pageRefs.current[p.page] = el; }}
                  className="relative w-full bg-white shadow-lg select-none"
                  style={{ cursor, aspectRatio: `${p.width_pts} / ${p.height_pts}` }}
                  onClick={(e) => handlePageClick(e, p.page)}
                  onMouseDown={(e) => handlePageMouseDown(e, p.page)}
                  onMouseMove={(e) => handlePageMouseMove(e, p.page)}
                  onMouseUp={(e) => handlePageMouseUp(e, p.page)}
                  data-testid={`bureau-editor-page-${p.page}`}
                >
                  <img
                    src={p.image_data_url}
                    alt={`Form page ${p.page + 1}`}
                    className="block w-full h-full pointer-events-none"
                    draggable={false}
                  />

                  {/* Rendered overlays */}
                  {overlays.map((ed) => {
                    if (ed.type === "text") {
                      const { left, top, scale } = ptsToDisplay(ed.page, ed.x, ed.y);
                      // insert_text baseline = y; the visible glyph sits
                      // ABOVE y, so shift the display div up by ~fontsize
                      // to line up with what the backend renders.
                      const fsPx = (ed.fontsize || 10) * scale;
                      return (
                        <div
                          key={ed.id}
                          onMouseDown={(e) => beginDrag(e, ed)}
                          onClick={(e) => { e.stopPropagation(); setSelectedId(ed.id); }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            const txt = window.prompt("Edit text:", ed.text);
                            if (txt === null) return;
                            setEdits((prev) => prev.map((it) =>
                              it.id === ed.id ? { ...it, text: txt } : it));
                          }}
                          className={`absolute font-sans whitespace-pre text-black ${
                            selectedId === ed.id ? "ring-2 ring-emerald-500 ring-offset-1" : ""
                          } cursor-move`}
                          style={{
                            left,
                            top: top - fsPx * 0.9,
                            fontSize: fsPx,
                            lineHeight: 1,
                            padding: "1px 2px",
                            background: selectedId === ed.id ? "rgba(16,185,129,0.10)" : "transparent",
                          }}
                          data-testid={`overlay-text-${ed.id}`}
                        >
                          {ed.text}
                        </div>
                      );
                    }
                    if (ed.type === "check") {
                      const { left, top, scale } = ptsToDisplay(ed.page, ed.x, ed.y);
                      const sz = (ed.size || 12) * scale;
                      return (
                        <div
                          key={ed.id}
                          onMouseDown={(e) => beginDrag(e, ed)}
                          onClick={(e) => { e.stopPropagation(); setSelectedId(ed.id); }}
                          className={`absolute font-bold text-black ${
                            selectedId === ed.id ? "ring-2 ring-emerald-500" : ""
                          } cursor-move flex items-center justify-center`}
                          style={{
                            left: left - sz * 0.5,
                            top: top - sz * 0.5,
                            width: sz,
                            height: sz,
                            fontSize: sz,
                            lineHeight: 1,
                            background: selectedId === ed.id ? "rgba(16,185,129,0.15)" : "transparent",
                          }}
                          data-testid={`overlay-check-${ed.id}`}
                        >
                          X
                        </div>
                      );
                    }
                    if (ed.type === "erase") {
                      const a = ptsToDisplay(ed.page, ed.x0, ed.y0);
                      const b = ptsToDisplay(ed.page, ed.x1, ed.y1);
                      const left = Math.min(a.left, b.left);
                      const top = Math.min(a.top, b.top);
                      const w = Math.abs(b.left - a.left);
                      const h = Math.abs(b.top - a.top);
                      return (
                        <div
                          key={ed.id}
                          onClick={(e) => { e.stopPropagation(); setSelectedId(ed.id); }}
                          className={`absolute bg-white ${
                            selectedId === ed.id ? "outline outline-2 outline-emerald-500" : "outline outline-1 outline-dashed outline-red-400/40"
                          } cursor-pointer`}
                          style={{ left, top, width: w, height: h }}
                          data-testid={`overlay-erase-${ed.id}`}
                        />
                      );
                    }
                    return null;
                  })}

                  {/* Live erase-drag preview */}
                  {eraseDraft && eraseDraft.page === p.page && (() => {
                    const a = ptsToDisplay(p.page, eraseDraft.startPtX, eraseDraft.startPtY);
                    const b = ptsToDisplay(p.page, eraseDraft.curPtX, eraseDraft.curPtY);
                    const left = Math.min(a.left, b.left);
                    const top = Math.min(a.top, b.top);
                    const w = Math.abs(b.left - a.left);
                    const h = Math.abs(b.top - a.top);
                    return (
                      <div
                        className="absolute bg-white/80 outline outline-2 outline-red-500 pointer-events-none"
                        style={{ left, top, width: w, height: h }}
                      />
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
